import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";

export type UnderlineTabDropdownOption = {
  id: string;
  name: string;
  icon?: React.ReactNode;
};

export type UnderlineTab = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  count?: number;
  ariaLabel?: string;
  dropdownOptions?: UnderlineTabDropdownOption[];
};

type UnderlineTabsProps = {
  tabs: UnderlineTab[];
  value: string;
  onValueChange: (id: string) => void;
  rightSlot?: React.ReactNode;
  className?: string;
  selectedFilters?: Record<string, string[]>;
  onFiltersChange?: (tabId: string, selectedIds: string[]) => void;
  /**
   * "underline" (default) — pill highlight + animated underline on a
   * bottom-bordered strip. "plain" — text-only tabs (active = n-90,
   * inactive = n-50) with no underline or border; labels are always
   * visible and counts / filter dropdowns are not rendered.
   */
  variant?: "underline" | "plain";
};

export const UnderlineTabs: React.FC<UnderlineTabsProps> = ({
  tabs,
  value,
  onValueChange,
  rightSlot,
  className,
  selectedFilters = {},
  onFiltersChange,
  variant = "underline",
}) => {
  const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const [underlineWidth, setUnderlineWidth] = useState(0);
  const [underlineLeft, setUnderlineLeft] = useState(0);

  const toggleFilter = (tabId: string, optionId: string) => {
    if (!onFiltersChange) return;

    const currentFilters = selectedFilters[tabId] || [];
    const newFilters = currentFilters.includes(optionId)
      ? currentFilters.filter((id) => id !== optionId)
      : [...currentFilters, optionId];

    onFiltersChange(tabId, newFilters);
  };

  useEffect(() => {
    if (variant === "plain") return;
    const compute = () => {
      const idx = tabs.findIndex((t) => t.id === value);
      const el = tabsRef.current[idx];
      if (!el) return;
      setUnderlineWidth(el.offsetWidth);
      setUnderlineLeft(el.offsetLeft);
    };
    compute();
    const id = requestAnimationFrame(compute);
    return () => cancelAnimationFrame(id);
  }, [tabs, value, variant]);

  // Recompute on window resize to keep underline aligned
  useEffect(() => {
    if (variant === "plain") return;
    const handle = () => {
      const idx = tabs.findIndex((t) => t.id === value);
      const el = tabsRef.current[idx];
      if (!el) return;
      setUnderlineWidth(el.offsetWidth);
      setUnderlineLeft(el.offsetLeft);
    };
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, [tabs, value, variant]);

  const renderedTabs = useMemo(
    () =>
      tabs.map((tab, i) => {
        const isActive = tab.id === value;

        if (variant === "plain") {
          return (
            <button
              key={tab.id}
              onClick={() => onValueChange(tab.id)}
              aria-label={tab.ariaLabel || tab.label}
              className={cn(
                "flex min-h-9 cursor-pointer items-center gap-2 rounded-md bg-transparent px-1 text-md font-medium whitespace-nowrap outline-none transition-colors",
                "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rust",
                isActive ? "text-n-90" : "text-n-50 hover:text-n-90",
              )}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          );
        }

        const hasDropdown =
          tab.dropdownOptions && tab.dropdownOptions.length > 0;
        const activeFilterCount = selectedFilters[tab.id]?.length || 0;

        const tabButton = (
          <button
            key={tab.id}
            ref={(el) => {
              if (el) {
                tabsRef.current[i] = el;
              }
            }}
            onClick={() => onValueChange(tab.id)}
            aria-label={tab.ariaLabel || tab.label}
            className={cn(
              "px-5 py-2.5 cursor-pointer rounded-none bg-transparent h-full border-0",
              "flex items-center gap-2 text-sm font-medium transition-all",
              "min-w-fit whitespace-nowrap",
              hasDropdown ? "rounded-tl-md" : "rounded-t-md",
              isActive
                ? "bg-primary/10 text-primary hover:bg-primary/15"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
            )}
          >
            {tab.icon}
            <span className="hidden lg:block">{tab.label}</span>
            {typeof tab.count === "number" && (
              <span
                className={cn(
                  "inline-flex items-center justify-center text-xs font-medium min-w-[18px] h-5 px-1.5 rounded-full",
                  isActive
                    ? "bg-primary/20 text-primary"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {tab.count}
              </span>
            )}
          </button>
        );

        if (!hasDropdown) {
          return (
            <div key={tab.id} className="flex items-center">
              {tabButton}
            </div>
          );
        }

        return (
          <div key={tab.id} className="flex items-center">
            {tabButton}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    "flex items-center gap-1 px-2 py-2.5 text-sm font-medium transition-all",
                    "rounded-tr-md border-l ",
                    isActive
                      ? "bg-primary/10 text-primary hover:bg-primary/15"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
                  )}
                >
                  <ChevronDown className="h-3 w-3" />
                  {activeFilterCount > 0 && (
                    <span className="bg-primary/20 text-primary text-xs px-1.5 py-0.5 rounded-full font-medium">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-48">
                <div className="p-2">
                  {tab.dropdownOptions?.map((option) => (
                    <div
                      key={option.id}
                      className="flex items-center space-x-2 p-1"
                    >
                      <Checkbox
                        id={`${tab.id}-${option.id}`}
                        checked={
                          selectedFilters[tab.id]?.includes(option.id) || false
                        }
                        onCheckedChange={() => toggleFilter(tab.id, option.id)}
                      />
                      <label
                        htmlFor={`${tab.id}-${option.id}`}
                        className="text-sm font-normal truncate flex-1 cursor-pointer flex items-center gap-2"
                        title={option.name}
                      >
                        {option.icon && (
                          <span className="text-muted-foreground flex-shrink-0">
                            {option.icon}
                          </span>
                        )}
                        <span className="truncate">{option.name}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      }),
    [tabs, value, onValueChange, selectedFilters, toggleFilter, variant],
  );

  if (variant === "plain") {
    return (
      <div className={cn("w-full", className)}>
        <div className="flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <div className="flex items-center gap-5">{renderedTabs}</div>
          {rightSlot}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("w-full", className)}>
      <div className="flex items-center justify-between w-full border-b ">
        <div className="relative flex items-center gap-0">
          {renderedTabs}
          <motion.div
            className="absolute bottom-[-1px] h-0.5 bg-foreground z-10"
            initial={false}
            animate={{ width: underlineWidth, left: underlineLeft }}
            transition={{ duration: 0.2 }}
          />
        </div>
        {rightSlot}
      </div>
    </div>
  );
};
